import { useEffect, useState } from "react";
import {
  ArrowLeft,
  BookOpen,
  ChevronDown,
  Cpu,
  Key,
  Terminal,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  generateApiDocsMarkdown,
  renderZodSchema,
} from "../../../src/apiDocs.ts";
import { webApi } from "../../../webApi.ts";
import { apiUrlPath } from "../../../constants.ts";
import { db } from "../db.ts";
import { api } from "../api.ts";

export const Docs = () => {
  const [markdown, setMarkdown] = useState("");
  const { user, isLoading: authLoading } = db.useAuth();
  const [userToken, setUserToken] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);
  const [expandedEndpoints, setExpandedEndpoints] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    setMarkdown(generateApiDocsMarkdown());
  }, []);

  useEffect(() => {
    if (user) {
      setTokenLoading(true);
      api({
        endpoint: "getApiToken",
        payload: {},
      })
        .then((res) => {
          setUserToken(res.token);
        })
        .catch((err) => {
          console.error("Failed to fetch API token:", err);
        })
        .finally(() => {
          setTokenLoading(false);
        });
    } else {
      setUserToken(null);
    }
  }, [user]);

  const toggleEndpoint = (name: string) => {
    setExpandedEndpoints((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleTocClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    name: string,
  ) => {
    e.preventDefault();
    setExpandedEndpoints((prev) => ({
      ...prev,
      [name]: true,
    }));
    setTimeout(() => {
      const el = document.getElementById(`endpoint-${name}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleMobileTocClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    name: string,
  ) => {
    e.preventDefault();
    setIsMobileTocOpen(false);
    setExpandedEndpoints((prev) => ({
      ...prev,
      [name]: true,
    }));
    setTimeout(() => {
      const el = document.getElementById(`endpoint-${name}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const parseInlineStyles = (text: string) => {
    const parts: React.ReactNode[] = [];
    let current = "";
    let i = 0;

    while (i < text.length) {
      if (text.substring(i, i + 2) === "**") {
        if (current) parts.push(current);
        current = "";
        const endIdx = text.indexOf("**", i + 2);
        if (endIdx !== -1) {
          parts.push(
            <strong key={`b-${i}`} className="font-semibold text-white">
              {text.substring(i + 2, endIdx)}
            </strong>,
          );
          i = endIdx + 2;
        } else {
          current += "**";
          i += 2;
        }
      } else if (text[i] === "`") {
        if (current) parts.push(current);
        current = "";
        const endIdx = text.indexOf("`", i + 1);
        if (endIdx !== -1) {
          parts.push(
            <code
              key={`code-${i}`}
              className="bg-white/10 px-1.5 py-0.5 rounded font-mono text-purple-300 text-xs"
            >
              {text.substring(i + 1, endIdx)}
            </code>,
          );
          i = endIdx + 1;
        } else {
          current += "`";
          i += 1;
        }
      } else {
        current += text[i];
        i++;
      }
    }
    if (current) parts.push(current);
    return parts;
  };

  const parsePropertyLine = (line: string) => {
    const indentMatch = line.match(/^(\s*)[-*]\s+/);
    if (!indentMatch) return null;
    const indentLevel = indentMatch[1].length;
    const content = line.substring(indentMatch[0].length);

    const nameMatch = content.match(/^\*\*([^*]+)\*\*/);
    if (!nameMatch) return null;
    const [nameWithStars] = nameMatch;
    const name = nameMatch[1];
    const rest = content.substring(nameWithStars.length);

    // Extract type and optional flags, e.g. " (string, optional, nullable) - Description"
    const typeMatch = rest.match(/^\s*\(([^)]+)\)/);
    let typeAndFlags = "any";
    let description = rest;
    if (typeMatch) {
      typeAndFlags = typeMatch[1];
      description = rest.substring(typeMatch[0].length);
    }

    // Clean up description prefix if it starts with " - " or ": " or " -: "
    description = description.replace(/^\s*[-:]+\s*/, "");

    return {
      indentLevel,
      name,
      typeAndFlags,
      description,
    };
  };

  const docEndpoints = Object.keys(webApi).filter((name) =>
    name !== "getApiToken"
  );

  const SchemaViewer = ({ schema }: { schema: unknown }) => {
    const rawMarkdown = renderZodSchema(schema);
    const lines = rawMarkdown.split("\n");
    const elements: React.ReactNode[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.match(/^\s*[-*]\s+/)) {
        const prop = parsePropertyLine(line);
        if (prop) {
          const { indentLevel, name, typeAndFlags, description } = prop;

          let badgeColor = "bg-pink-500/10 text-pink-400 border-pink-500/20";
          let cleanType = typeAndFlags;
          let isOptional = false;
          let isNullable = false;

          if (cleanType.includes("optional")) {
            isOptional = true;
            cleanType = cleanType.replace(/\(?optional\)?/, "").trim();
          }
          if (cleanType.includes("nullable")) {
            isNullable = true;
            cleanType = cleanType.replace(/\(?nullable\)?/, "").trim();
          }
          cleanType = cleanType.replace(/,\s*$/, "").trim();

          if (cleanType.startsWith("string")) {
            badgeColor = "bg-blue-500/10 text-blue-400 border-blue-500/20";
          } else if (cleanType.startsWith("number")) {
            badgeColor =
              "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
          } else if (cleanType.startsWith("boolean")) {
            badgeColor = "bg-amber-500/10 text-amber-400 border-amber-500/20";
          } else if (cleanType.startsWith("object")) {
            badgeColor =
              "bg-purple-500/10 text-purple-400 border-purple-500/20";
          } else if (
            cleanType.includes("[]") || cleanType.startsWith("Array")
          ) {
            badgeColor =
              "bg-indigo-500/10 text-indigo-400 border-indigo-500/20";
          }

          elements.push(
            <div
              key={`prop-${i}`}
              className="my-1.5 text-sm leading-relaxed"
              style={{ paddingLeft: `${indentLevel * 12}px` }}
            >
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 py-0.5">
                <span className="font-mono font-bold text-slate-200">
                  {name}
                </span>
                <span
                  className={`px-1.5 py-0.5 rounded border text-[10px] font-mono leading-none ${badgeColor}`}
                >
                  {cleanType}
                </span>
                {isOptional && (
                  <span className="text-[9px] text-gray-500 font-mono tracking-wider uppercase bg-white/5 border border-white/10 px-1 py-0.5 rounded leading-none">
                    opt
                  </span>
                )}
                {isNullable && (
                  <span className="text-[9px] text-gray-500 font-mono tracking-wider uppercase bg-white/5 border border-white/10 px-1 py-0.5 rounded leading-none font-bold">
                    null
                  </span>
                )}
                {description && (
                  <span className="text-gray-400 text-xs ml-1 flex-1 min-w-[200px]">
                    &mdash; {parseInlineStyles(description)}
                  </span>
                )}
              </div>
            </div>,
          );
        }
      }
    }
    return <div className="space-y-1">{elements}</div>;
  };

  const EndpointCard = (
    { name, endpoint }: {
      name: string;
      endpoint: { input: unknown; output: unknown };
    },
  ) => {
    const isExpanded = !!expandedEndpoints[name];
    return (
      <section
        id={`endpoint-${name}`}
        className="border border-white/5 bg-white/[0.01] hover:bg-white/[0.015] rounded-2xl p-6 transition-all hover:border-purple-500/20 shadow-lg relative overflow-hidden group border-l-4 border-l-purple-500/60 scroll-mt-6"
      >
        <button
          type="button"
          onClick={() => toggleEndpoint(name)}
          className="flex items-center justify-between w-full text-left focus:outline-none"
        >
          <div className="flex items-center gap-3">
            <span className="bg-purple-600/10 text-purple-400 px-2.5 py-1 rounded-lg text-xs font-bold font-mono uppercase">
              POST
            </span>
            <h3 className="text-lg md:text-xl font-bold font-mono text-purple-300">
              {name}
            </h3>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 font-mono hidden sm:inline">
              {apiUrlPath}
            </span>
            <ChevronDown
              size={18}
              className={`text-gray-400 transition-transform duration-200 ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          </div>
        </button>

        {isExpanded && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6 pt-6 border-t border-white/5 transition-all">
            {/* Request Payload */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-white/5">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                <h4 className="text-xs font-bold uppercase tracking-wider text-blue-400">
                  Request Payload
                </h4>
              </div>
              <SchemaViewer schema={endpoint.input} />
            </div>

            {/* Response Payload */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-white/5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400">
                </span>
                <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-400">
                  Response Payload
                </h4>
              </div>
              <SchemaViewer schema={endpoint.output} />
            </div>
          </div>
        )}
      </section>
    );
  };

  const parts = markdown.split("## Endpoints");
  const topMarkdown = parts[0] || "";

  const renderTopContent = () => {
    const lines = topMarkdown.split("\n");
    const elements: React.ReactNode[] = [];
    let inCodeBlock = false;
    let codeBlockContent: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.startsWith("```")) {
        if (inCodeBlock) {
          const codeText = codeBlockContent.join("\n");
          elements.push(
            <pre
              key={`code-${i}`}
              className="bg-black/40 border border-purple-500/20 rounded-xl p-4 my-4 font-mono text-sm text-purple-200 overflow-x-auto max-w-full"
            >
              <code>{codeText}</code>
            </pre>,
          );
          codeBlockContent = [];
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
        }
        continue;
      }

      if (inCodeBlock) {
        codeBlockContent.push(line);
        continue;
      }

      if (line.startsWith("# ")) {
        elements.push(
          <h1
            key={`h1-${i}`}
            id="overview"
            className="text-4xl font-extrabold mb-6 mt-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-red-500"
          >
            {line.substring(2)}
          </h1>,
        );
      } else if (line.startsWith("## ")) {
        const title = line.substring(3).trim();
        const headingId = title.toLowerCase().replace(/\s+/g, "-");
        elements.push(
          <h2
            key={`h2-${i}`}
            id={headingId}
            className="text-2xl font-bold mb-4 mt-8 text-white border-b border-white/10 pb-2 flex items-center gap-2 scroll-mt-6"
          >
            {title === "Authentication"
              ? <Key size={20} className="text-purple-400" />
              : null}
            {title === "Server URL"
              ? <Terminal size={20} className="text-purple-400" />
              : null}
            {title === "Request Format"
              ? <Cpu size={20} className="text-purple-400" />
              : null}
            {title}
          </h2>,
        );
      } else if (line.startsWith("### ")) {
        const headingId = line.substring(4).replace(/[^a-zA-Z0-9-]/g, "")
          .trim();
        elements.push(
          <h3
            key={`h3-${i}`}
            id={`endpoint-${headingId}`}
            className="text-xl font-semibold mb-3 mt-6 text-purple-300 scroll-mt-6"
          >
            {parseInlineStyles(line.substring(4))}
          </h3>,
        );
      } else if (line.startsWith("#### ")) {
        elements.push(
          <h4
            key={`h4-${i}`}
            className="text-lg font-medium mb-2 mt-4 text-gray-200"
          >
            {line.substring(5)}
          </h4>,
        );
      } else if (line.match(/^\s*[-*]\s+/)) {
        const prop = parsePropertyLine(line);
        if (prop) {
          const { indentLevel, name, typeAndFlags, description } = prop;

          let badgeColor = "bg-pink-500/10 text-pink-400 border-pink-500/20";
          let cleanType = typeAndFlags;
          let isOptional = false;
          let isNullable = false;

          if (cleanType.includes("optional")) {
            isOptional = true;
            cleanType = cleanType.replace(/\(?optional\)?/, "").trim();
          }
          if (cleanType.includes("nullable")) {
            isNullable = true;
            cleanType = cleanType.replace(/\(?nullable\)?/, "").trim();
          }
          cleanType = cleanType.replace(/,\s*$/, "").trim();

          if (cleanType.startsWith("string")) {
            badgeColor = "bg-blue-500/10 text-blue-400 border-blue-500/20";
          } else if (cleanType.startsWith("number")) {
            badgeColor =
              "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
          } else if (cleanType.startsWith("boolean")) {
            badgeColor = "bg-amber-500/10 text-amber-400 border-amber-500/20";
          } else if (cleanType.startsWith("object")) {
            badgeColor =
              "bg-purple-500/10 text-purple-400 border-purple-500/20";
          } else if (
            cleanType.includes("[]") || cleanType.startsWith("Array")
          ) {
            badgeColor =
              "bg-indigo-500/10 text-indigo-400 border-indigo-500/20";
          }

          elements.push(
            <div
              key={`prop-${i}`}
              className="my-1.5 text-sm leading-relaxed"
              style={{ paddingLeft: `${indentLevel * 12}px` }}
            >
              <div className="flex flex-wrap items-baseline gap-2 py-0.5">
                <span className="font-mono font-bold text-slate-100">
                  {name}
                </span>
                <span
                  className={`px-1.5 py-0.5 rounded border text-[11px] font-mono leading-none ${badgeColor}`}
                >
                  {cleanType}
                </span>
                {isOptional && (
                  <span className="text-[9px] text-gray-500 font-mono tracking-wider uppercase bg-white/5 border border-white/5 px-1 py-0.5 rounded leading-none">
                    optional
                  </span>
                )}
                {isNullable && (
                  <span className="text-[9px] text-gray-500 font-mono tracking-wider uppercase bg-white/5 border border-white/5 px-1 py-0.5 rounded leading-none font-bold">
                    nullable
                  </span>
                )}
                {description && (
                  <span className="text-gray-400 text-xs ml-1 flex-1 min-w-[200px]">
                    &mdash; {parseInlineStyles(description)}
                  </span>
                )}
              </div>
            </div>,
          );
          continue;
        } else {
          const cleanText = line.replace(/^\s*[-*]\s+/, "");
          elements.push(
            <li
              key={`li-${i}`}
              className="text-sm text-gray-300 ml-6 list-disc mb-1.5"
            >
              {parseInlineStyles(cleanText)}
            </li>,
          );
          continue;
        }
      } else if (line.trim() === "") {
        continue;
      } else {
        elements.push(
          <p
            key={`p-${i}`}
            className="mb-4 text-gray-300 leading-relaxed text-sm"
          >
            {parseInlineStyles(line)}
          </p>,
        );
      }
    }

    return elements;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black text-white">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <Link
          to="/"
          className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="mr-2" size={18} /> Back to Home
        </Link>

        {authLoading
          ? (
            <div className="glass-card p-6 border border-white/10 bg-white/[0.01] rounded-xl mb-8 flex justify-center items-center">
              <span className="text-sm text-gray-400 font-medium">
                Checking authentication...
              </span>
            </div>
          )
          : user
          ? (
            <div className="glass-card p-6 border border-purple-500/30 bg-purple-950/10 rounded-xl mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-purple-300 flex items-center gap-2">
                  <Key size={18} /> Your Developer API Token
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  Use this token in your requests or scripts. Keep it secure.
                </p>
              </div>
              <div className="flex-1 max-w-md font-mono text-xs bg-black/60 px-4 py-3 rounded-lg border border-purple-500/20 text-purple-200 select-all overflow-x-auto break-all relative group">
                {tokenLoading
                  ? "Generating token..."
                  : (userToken || "No token available")}
              </div>
            </div>
          )
          : (
            <div className="glass-card p-6 border border-white/10 bg-white/[0.01] rounded-xl mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-gray-300 flex items-center gap-2">
                  <Key size={18} /> Developer API Token
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  Log in to view or generate your personalized developer API
                  token.
                </p>
              </div>
              <Link
                to="/app"
                className="px-6 py-2.5 rounded-full text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white text-center transition-all shadow-lg shadow-purple-500/20"
              >
                Log In / Sign Up
              </Link>
            </div>
          )}

        {/* Dynamic Responsive Layout */}
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Mobile TOC Accordion */}
          <div className="w-full bg-white/[0.02] border border-white/5 backdrop-blur-xl rounded-2xl p-4 block md:hidden mb-4">
            <button
              type="button"
              onClick={() => setIsMobileTocOpen(!isMobileTocOpen)}
              className="flex items-center justify-between w-full text-sm font-bold text-gray-300 uppercase tracking-wider"
            >
              <div className="flex items-center gap-2">
                <BookOpen size={16} className="text-purple-400" />
                <span>Table of Contents</span>
              </div>
              <ChevronDown
                size={16}
                className={`text-gray-400 transition-transform duration-200 ${
                  isMobileTocOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {isMobileTocOpen && (
              <ul className="space-y-2 text-sm mt-4 pt-4 border-t border-white/5">
                <li>
                  <a
                    href="#overview"
                    onClick={() => setIsMobileTocOpen(false)}
                    className="block text-gray-300 hover:text-purple-400 py-1"
                  >
                    Overview
                  </a>
                </li>
                <li>
                  <a
                    href="#server-url"
                    onClick={() => setIsMobileTocOpen(false)}
                    className="block text-gray-300 hover:text-purple-400 py-1"
                  >
                    Server URL
                  </a>
                </li>
                <li>
                  <a
                    href="#authentication"
                    onClick={() => setIsMobileTocOpen(false)}
                    className="block text-gray-300 hover:text-purple-400 py-1"
                  >
                    Authentication
                  </a>
                </li>
                <li>
                  <a
                    href="#request-format"
                    onClick={() => setIsMobileTocOpen(false)}
                    className="block text-gray-300 hover:text-purple-400 py-1"
                  >
                    Request Format
                  </a>
                </li>
                <li>
                  <a
                    href="#partner-event-submission-webhooks"
                    onClick={() => setIsMobileTocOpen(false)}
                    className="block text-gray-300 hover:text-purple-400 py-1"
                  >
                    Partner Webhooks
                  </a>
                </li>
                <li>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-4 mb-2">
                    Endpoints
                  </div>
                  <ul className="pl-3 border-l border-white/10 space-y-2 mt-1">
                    {docEndpoints.map((name) => (
                      <li key={name}>
                        <a
                          href={`#endpoint-${name}`}
                          onClick={(e) => handleMobileTocClick(e, name)}
                          className="block text-gray-400 hover:text-purple-300 font-mono text-xs py-0.5 truncate"
                        >
                          {name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
            )}
          </div>

          {/* Sticky Left TOC Sidebar */}
          <nav className="w-full lg:w-64 shrink-0 lg:sticky lg:top-8 bg-white/[0.02] border border-white/5 backdrop-blur-xl rounded-2xl p-6 hidden md:block">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <BookOpen size={16} className="text-purple-400" />{" "}
              Table of Contents
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="#overview"
                  className="block text-gray-300 hover:text-purple-400 transition-colors py-1 font-medium"
                >
                  Overview
                </a>
              </li>
              <li>
                <a
                  href="#server-url"
                  className="block text-gray-300 hover:text-purple-400 transition-colors py-1 font-medium"
                >
                  Server URL
                </a>
              </li>
              <li>
                <a
                  href="#authentication"
                  className="block text-gray-300 hover:text-purple-400 transition-colors py-1 font-medium"
                >
                  Authentication
                </a>
              </li>
              <li>
                <a
                  href="#request-format"
                  className="block text-gray-300 hover:text-purple-400 transition-colors py-1 font-medium"
                >
                  Request Format
                </a>
              </li>
              <li>
                <a
                  href="#partner-event-submission-webhooks"
                  className="block text-gray-300 hover:text-purple-400 transition-colors py-1 font-medium"
                >
                  Partner Webhooks
                </a>
              </li>
              <li>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-6 mb-2">
                  Endpoints
                </div>
                <ul className="pl-3 border-l border-white/10 space-y-2 mt-1">
                  {docEndpoints.map((name) => (
                    <li key={name}>
                      <a
                        href={`#endpoint-${name}`}
                        onClick={(e) => handleTocClick(e, name)}
                        className="block text-gray-400 hover:text-purple-300 font-mono text-xs transition-colors py-0.5 truncate"
                      >
                        {name}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>
          </nav>

          {/* Core Content Container */}
          <div className="flex-1 w-full space-y-8">
            {/* Top General Guidelines card */}
            <div className="glass-card p-8 md:p-12 rounded-2xl border border-white/5 bg-white/[0.02] backdrop-blur-xl shadow-2xl">
              <div className="prose prose-invert max-w-none">
                {renderTopContent()}
              </div>
            </div>

            {/* Endpoints section header */}
            <div id="endpoints" className="pt-4 scroll-mt-6">
              <h2 className="text-2xl font-extrabold text-white flex items-center gap-2 pb-2 border-b border-white/10">
                <BookOpen className="text-purple-400" size={24} />{" "}
                API Endpoints Reference
              </h2>
              <p className="text-sm text-gray-400 mt-2">
                Detailed schemas and parameters for all available Developer API
                actions.
              </p>
            </div>

            {/* Endpoint Cards (Beautiful, distinct separation) */}
            <div className="space-y-8">
              {Object.entries(webApi).map(([name, endpoint]) => {
                if (name === "getApiToken") return null;
                return (
                  <EndpointCard key={name} name={name} endpoint={endpoint} />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
